import React from 'react';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';

/**
 * Error Boundary for Recovery Plan Components
 * Provides graceful error handling and recovery options
 */
class RecoveryPlanErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError() {
    // Update state to show error UI
    return {
      hasError: true,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('RecoveryPlan Error Boundary:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString()
    });

    this.setState({
      error,
      errorInfo
    });

    // In production, you might want to send this to an error reporting service
    if (typeof window !== 'undefined' && window.process?.env?.NODE_ENV === 'production') {
      // Example: Send to error reporting service
      // errorReportingService.captureException(error, { extra: errorInfo });
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleClose = () => {
    if (this.props.onClose) {
      this.props.onClose();
    } else {
      // Fallback - try to reset the boundary
      this.handleRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      // Error UI
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-red-500" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Recovery Plan Error</h2>
                  <p className="text-sm text-gray-500">Error ID: {this.state.errorId}</p>
                </div>
              </div>
              <button
                onClick={this.handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                We encountered an unexpected error while processing your recovery plan.
                This issue has been logged and your data is safe.
              </p>

              {(typeof window !== 'undefined' && window.process?.env?.NODE_ENV === 'development') && this.state.error && (
                <details className="mb-4">
                  <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">
                    Technical Details (Development Mode)
                  </summary>
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg border text-xs font-mono">
                    <div className="text-red-600 font-semibold mb-2">
                      {this.state.error.message}
                    </div>
                    <div className="text-gray-600 whitespace-pre-wrap">
                      {this.state.error.stack}
                    </div>
                  </div>
                </details>
              )}

              <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-azure-900 mb-2">What you can do:</h3>
                <ul className="text-sm text-azure-800 space-y-1">
                  <li>• Try refreshing the recovery plan</li>
                  <li>• Check that your task data is valid</li>
                  <li>• Contact support if the problem persists</li>
                  <li>• Your existing tasks and timeline are unaffected</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={this.handleClose}
                className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={this.handleRetry}
                className="flex-1 px-4 py-2 text-sm bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

export default RecoveryPlanErrorBoundary;